/**
 * Context Utilities
 * Helper functions for managing user contexts and navigation
 * NOW TENANT-AWARE: Stores context data per tenant in sessionStorage
 */

import { tokenManager } from '@/utils/tokenManager'
import { getTenantStorageKey, getTenantFromHostname } from './tenant.util'

export interface UserContext {
  type: 'PLATFORM' | 'ORGANIZATION' | 'BRANCH' | 'SELF' | 'ALUMNI'
  orgId?: string
  orgName?: string
  orgSlug?: string
  branchId?: string
  branchName?: string
  branchSlug?: string
}

/**
 * Parse contexts array from login response
 */
export function parseContexts(contexts: UserContext[] | undefined): UserContext[] {
  if (!contexts || !Array.isArray(contexts)) {
    return []
  }
  return contexts
}

/**
 * Get default context from contexts array
 * Priority: Single context > First context in array
 */
export function getDefaultContext(contexts: UserContext[]): UserContext | null {
  if (contexts.length === 0) {
    return null
  }
  
  if (contexts.length === 1) {
    return contexts[0]
  }
  
  // Multiple contexts - return first one (user will select)
  // Could also return the most recent one based on usage history
  return contexts[0]
}

/**
 * Build navigation path from context
 */
export function buildContextNavigationPath(context: UserContext): string {
  switch (context.type) {
    case 'PLATFORM':
      return '/platform'
    
    case 'ORGANIZATION':
      if (context.orgSlug) {
        return `/orgs/${context.orgSlug}`
      }
      // Fallback: navigate to orgs list if slug not available
      return '/orgs'
    
    case 'BRANCH':
      if (context.orgSlug && context.branchSlug) {
        return `/orgs/${context.orgSlug}/branches/${context.branchSlug}`
      }
      if (context.orgSlug) {
        return `/orgs/${context.orgSlug}`
      }
      return '/orgs'
    
    case 'SELF':
      if (context.orgSlug) {
        return `/orgs/${context.orgSlug}/self`
      }
      return '/'
    
    case 'ALUMNI':
      if (context.orgSlug) {
        return `/orgs/${context.orgSlug}/alumni`
      }
      return '/'
    
    default:
      return '/'
  }
}

/**
 * Store context token in both localStorage and cookies
 */
export function storeContextToken(contextToken: string): void {
  tokenManager.setToken('contextToken', contextToken, { expires: 7 })
}

/**
 * Get context token from storage
 * Uses tokenManager which checks localStorage first, then cookies
 */
export function getContextToken(): string | null {
  return tokenManager.getToken('contextToken')
}

/**
 * Clear context token from localStorage
 */
export function clearContextToken(): void {
  tokenManager.removeToken('contextToken')
}

/**
 * Store context data in sessionStorage
 * NOW TENANT-AWARE: Stores context with tenant prefix
 */
export function storeContext(context: UserContext): void {
  const tenantId = getTenantFromHostname()
  const tenantKey = getTenantStorageKey('currentContext', tenantId)
  
  sessionStorage.setItem(tenantKey, JSON.stringify(context))
  
  // Also store in global key for backward compatibility during migration
  sessionStorage.setItem('currentContext', JSON.stringify(context))
  
  // Store org/branch slugs for backwards compatibility (tenant-aware)
  if (context.orgSlug) {
    const orgSlugKey = getTenantStorageKey('currentOrgSlug', tenantId)
    sessionStorage.setItem(orgSlugKey, context.orgSlug)
    sessionStorage.setItem('currentOrgSlug', context.orgSlug) // Global for compatibility
  }
  if (context.branchSlug) {
    const branchSlugKey = getTenantStorageKey('currentBranchSlug', tenantId)
    sessionStorage.setItem(branchSlugKey, context.branchSlug)
    sessionStorage.setItem('currentBranchSlug', context.branchSlug) // Global for compatibility
  }
  
  // Migration cleanup: remove from localStorage if exists
  try {
    localStorage.removeItem(tenantKey)
    localStorage.removeItem('currentContext')
    if (context.orgSlug) {
      const orgSlugKey = getTenantStorageKey('currentOrgSlug', tenantId)
      localStorage.removeItem(orgSlugKey)
      localStorage.removeItem('currentOrgSlug')
    }
    if (context.branchSlug) {
      const branchSlugKey = getTenantStorageKey('currentBranchSlug', tenantId)
      localStorage.removeItem(branchSlugKey)
      localStorage.removeItem('currentBranchSlug')
    }
  } catch (migrationError) {
    // Silent error handling
  }
}

/**
 * Get stored context from sessionStorage
 * NOW TENANT-AWARE: Retrieves context from tenant-specific storage
 */
export function getStoredContext(): UserContext | null {
  const tenantId = getTenantFromHostname()
  const tenantKey = getTenantStorageKey('currentContext', tenantId)
  
  // Try tenant-specific key in sessionStorage first
  const tenantContextStr = sessionStorage.getItem(tenantKey)
  if (tenantContextStr) {
    return JSON.parse(tenantContextStr)
  }
  
  // Fallback to global key in sessionStorage for backward compatibility
  const contextStr = sessionStorage.getItem('currentContext')
  if (contextStr) {
    const context = JSON.parse(contextStr)
    // Migrate to tenant-specific key
    storeContext(context)
    return context
  }
  
  // Migration: check localStorage for existing context
  try {
    const localStorageTenantContextStr = localStorage.getItem(tenantKey)
    if (localStorageTenantContextStr) {
      const context = JSON.parse(localStorageTenantContextStr)
      // Migrate to sessionStorage
      storeContext(context)
      return context
    }
    
    const localStorageContextStr = localStorage.getItem('currentContext')
    if (localStorageContextStr) {
      const context = JSON.parse(localStorageContextStr)
      // Migrate to sessionStorage
      storeContext(context)
      return context
    }
  } catch (migrationError) {
    // Silent error handling
  }
  
  return null
}

/**
 * Get stored contexts array from sessionStorage
 */
export function getContexts(): UserContext[] {
  // Try sessionStorage first
  const contextsStr = sessionStorage.getItem('contexts')
  if (contextsStr) {
    return JSON.parse(contextsStr)
  }
  
  // Migration: check localStorage
  try {
    const localStorageContextsStr = localStorage.getItem('contexts')
    if (localStorageContextsStr) {
      const contexts = JSON.parse(localStorageContextsStr)
      // Migrate to sessionStorage
      sessionStorage.setItem('contexts', localStorageContextsStr)
      // Remove from localStorage
      localStorage.removeItem('contexts')
      return contexts
    }
  } catch (migrationError) {
    // Silent error handling
  }
  
  return []
}

/**
 * Check if user has multiple contexts
 */
export function hasMultipleContexts(contexts: UserContext[]): boolean {
  return contexts.length > 1
}

/**
 * Check if user has single context
 */
export function hasSingleContext(contexts: UserContext[]): boolean {
  return contexts.length === 1
}

/**
 * Check if user has no contexts
 */
export function hasNoContexts(contexts: UserContext[]): boolean {
  return contexts.length === 0
}

/**
 * Get context display name
 */
export function getContextDisplayName(context: UserContext): string {
  switch (context.type) {
    case 'PLATFORM':
      return 'Platform'
    case 'ORGANIZATION':
      return context.orgName || 'Organization'
    case 'BRANCH':
      return context.branchName 
        ? `${context.orgName} - ${context.branchName}`
        : context.orgName || 'Branch'
    case 'SELF':
      return context.orgName ? `${context.orgName} - Self Service` : 'Self Service'
    case 'ALUMNI':
      return context.orgName ? `${context.orgName} - Alumni` : 'Alumni'
    default:
      return 'Unknown'
  }
}

/**
 * Format context for display in selector
 */
export function formatContextForDisplay(context: UserContext): {
  id: string
  type: string
  name: string
  description: string
} {
  // Generate unique ID using slugs (which are unique) instead of IDs (which might be undefined)
  // Format: TYPE-orgSlug-branchSlug or TYPE-orgSlug or TYPE-platform
  let id: string
  if (context.type === 'PLATFORM') {
    id = 'PLATFORM-platform'
  } else if (context.type === 'BRANCH') {
    const orgSlug = context.orgSlug || 'unknown'
    const branchSlug = context.branchSlug || 'unknown'
    id = `BRANCH-${orgSlug}-${branchSlug}`
  } else if (context.type === 'ORGANIZATION') {
    const orgSlug = context.orgSlug || 'unknown'
    id = `ORGANIZATION-${orgSlug}`
  } else {
    // Fallback to IDs if slugs not available
    id = `${context.type}-${context.orgId || 'platform'}${context.branchId ? `-${context.branchId}` : ''}`
  }
  
  return {
    id,
    type: context.type,
    name: getContextDisplayName(context),
    description: context.type === 'PLATFORM' 
      ? 'Platform administration' 
      : context.type === 'BRANCH'
      ? `${context.orgName} - Branch access`
      : `${context.orgName} - Organization access`,
  }
}

